import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Eye, EyeOff, ArrowRight, Check } from "lucide-react";
import { useGoogleLogin } from "@react-oauth/google";
import {
  getMailForPasswordReset,
  userLogin,
  verifyGoogle,
} from "../../../reducer/authSlice";
import { getUserProfile } from "../../../reducer/profileSlice";
import { checkNotAdmin } from "../../../utils/utils";
import { defaultPath } from "../../../utils/constants";
import toast from "../../Toast/toast";
import Modal from "../../Modal/Modal";
import AuthLayout, {
  GoogleMark,
  authInputClass,
  authPrimaryBtnClass,
  authSecondaryBtnClass,
} from "components/app/AuthLayout";

const SignIn = () => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm({ mode: "onChange" });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [timer, setTimer] = useState(0);
  const [displayText, setDisplayText] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(() => {
    try {
      return localStorage.getItem("qreativs-keep-signed-in") === "1";
    } catch {
      return false;
    }
  });

  const emailValue = watch("email");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onSubmit = async (data) => {
    const payload = {
      email: data.email.trim(),
      password: data.password.trim(),
      isAdmin: !checkNotAdmin(),
    };

    try {
      localStorage.setItem(
        "qreativs-keep-signed-in",
        keepSignedIn ? "1" : "0"
      );
    } catch {
      /* ignore */
    }

    setSubmitting(true);
    try {
      const status = await dispatch(userLogin(payload)).unwrap();
      if (status) {
        reset();
        const { payload: profile } = await dispatch(getUserProfile());

        if (!checkNotAdmin()) {
          navigate("/dashboard");
        } else if (profile?.userType) {
          navigate(defaultPath);
        } else {
          navigate("/signup?profilePending=true");
        }
      }
    } catch (error) {
      console.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalYes = async () => {
    setIsModalOpen(false);
    const payload = { email: emailValue };
    try {
      const status = await dispatch(getMailForPasswordReset(payload)).unwrap();
      if (status === true) {
        setDisplayText(true);
        setIsSubmitDisabled(true);
        setTimer(30);
        toast.success("Reset link sent to your email successfully!");
      } else {
        toast.error("Failed to send reset link. Please try again.");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong. Try again!");
    }
  };

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    } else {
      setIsSubmitDisabled(false);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleGoogleSuccess = async (res) => {
    const authCode = res.code;
    try {
      const status = await dispatch(verifyGoogle({ code: authCode })).unwrap();
      if (status) {
        if (status?.isNewUser) {
          navigate("/signup/google");
          return;
        }
        const { payload: profile } = await dispatch(getUserProfile());
        if (!checkNotAdmin()) {
          navigate("/dashboard");
        } else if (profile?.userType) {
          navigate(defaultPath);
        } else {
          navigate("/signup?profilePending=true");
        }
      }
    } catch (err) {
      toast.error("Google Sign-in failed");
    }
  };

  const loginWithGoogle = useGoogleLogin({
    flow: "auth-code",
    onSuccess: handleGoogleSuccess,
    onError: () => console.log("Google Login Failed"),
  });

  return (
    <AuthLayout
      eyebrow="Welcome back"
      title="Sign in to Qreativs"
      subtitle="Pick up where you left off — your network is waiting."
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <div>
          <label
            htmlFor="email"
            className="block text-sm text-zinc-300 mb-2 font-medium"
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            placeholder="Enter email"
            className={authInputClass}
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email format",
              },
              validate: (value) => {
                const trimmedValue = value.trim();
                if (value !== trimmedValue) {
                  return "Email cannot have leading or trailing spaces";
                }
                if (/\s/.test(value)) {
                  return "Email cannot contain spaces in between";
                }
                const disallowedSpecialChars =
                  /[!#$%^&*()+=\[\]{}|\\:;"'<>,?/]/;
                if (disallowedSpecialChars.test(value)) {
                  return "Email cannot contain special characters like !, #, $, %, etc.";
                }
                return true;
              },
            })}
          />
          {errors.email && (
            <p className="mt-1.5 text-xs text-red-400">{errors.email.message}</p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label
              htmlFor="password"
              className="text-sm text-zinc-300 font-medium"
            >
              Password
            </label>
            <button
              type="button"
              onClick={() => navigate("/forgot-password")}
              className={`text-xs text-[#8b83ff] hover:text-[#6C63F5] transition-colors ${
                isSubmitDisabled ? "opacity-50 pointer-events-none" : ""
              }`}
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              autoComplete="current-password"
              className={`${authInputClass} pr-11`}
              {...register("password", { required: "Password is required" })}
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              aria-label="Toggle password visibility"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-red-400">
              {errors.password.message}
            </p>
          )}
          {displayText && (
            <p className="mt-2 text-xs text-zinc-400">
              The link has been sent to your email. Please check your inbox.
            </p>
          )}
        </div>

        <label className="auth-keep-signed group flex items-center gap-3 select-none cursor-pointer w-fit">
          <span className="relative inline-flex">
            <input
              type="checkbox"
              checked={keepSignedIn}
              onChange={(e) => setKeepSignedIn(e.target.checked)}
              className="peer sr-only"
            />
            <span
              aria-hidden
              className={`auth-keep-signed-box flex h-5 w-5 items-center justify-center rounded-md border transition-all duration-200 ${
                keepSignedIn
                  ? "border-[#6C63F5] bg-[#6C63F5] shadow-[0_0_0_3px_rgba(108,99,245,0.2),0_4px_12px_-2px_rgba(108,99,245,0.45)]"
                  : "border-zinc-700 bg-zinc-900/80 group-hover:border-zinc-500"
              }`}
            >
              <Check
                size={12}
                strokeWidth={3}
                className={`text-white transition-all duration-200 ${
                  keepSignedIn
                    ? "opacity-100 scale-100"
                    : "opacity-0 scale-75"
                }`}
              />
            </span>
          </span>
          <span
            className={`text-sm transition-colors duration-200 ${
              keepSignedIn
                ? "text-zinc-100 font-medium"
                : "text-zinc-400 group-hover:text-zinc-300"
            }`}
          >
            Keep me signed in
          </span>
        </label>

        <button
          type="submit"
          disabled={!isValid || isSubmitDisabled || submitting}
          className={authPrimaryBtnClass}
        >
          {isSubmitDisabled
            ? `Wait ${timer}s`
            : submitting
              ? "Signing in…"
              : "Sign in"}
          {!submitting && !isSubmitDisabled && (
            <ArrowRight
              size={16}
              className="transition-transform group-hover:translate-x-0.5"
            />
          )}
        </button>

        <div className="flex items-center gap-4 py-1">
          <div className="h-px flex-1 bg-zinc-800" />
          <span className="text-xs text-zinc-500">or</span>
          <div className="h-px flex-1 bg-zinc-800" />
        </div>

        <button
          type="button"
          onClick={() => loginWithGoogle()}
          className={authSecondaryBtnClass}
        >
          <GoogleMark />
          Continue with Google
        </button>

        {checkNotAdmin() && (
          <p className="text-center text-sm text-zinc-400 pt-2">
            New to Qreativs?{" "}
            <Link
              to="/signup"
              className="text-[#8b83ff] hover:text-[#6C63F5] font-medium transition-colors"
            >
              Create an account
            </Link>
          </p>
        )}
      </form>

      {isModalOpen && (
        <Modal
          onClose={() => setIsModalOpen(false)}
          heading="Forgot Password ?"
          modalOpen={isModalOpen}
          width="20rem"
        >
          <div className="space-y-4">
            <p className="text-sm text-zinc-300">
              Are you sure you want to Reset Password?
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                className="flex-1 rounded-full bg-[#6C63F5] text-white py-2.5 text-sm font-semibold"
                onClick={handleModalYes}
              >
                Yes
              </button>
              <button
                type="button"
                className="flex-1 rounded-full border border-zinc-700 text-zinc-200 py-2.5 text-sm font-semibold"
                onClick={() => setIsModalOpen(false)}
              >
                No
              </button>
            </div>
          </div>
        </Modal>
      )}
    </AuthLayout>
  );
};

export default SignIn;
